import { useState } from 'react';
import { UserPlus, Plus, X } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { EmptyState } from '@/components/EmptyState';
import { ProvisioningStatusPanel } from '@/components/ProvisioningStatusPanel';
import { post } from '@/lib/api';

interface Onboarding {
  id: string;
  employee_name: string;
  status: string;
  start_date: string;
  target_completion_date: string;
  progress: number;
  tasks: Array<{ id: string; title: string; completed: boolean }>;
  assigned_buddy_name?: string;
}

export function OnboardingPage() {
  const { data, loading, refetch } = useApi<{ onboarding: Onboarding[] }>('onboarding');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [onboardForm, setOnboardForm] = useState({
    employee_id: '',
    employee_name: '',
    start_date: '',
    target_completion_date: '',
    assigned_buddy_name: '',
    template_id: '',
  });

  async function handleCreate() {
    if (!onboardForm.employee_id || !onboardForm.start_date) {
      setCreateError('Employee ID and start date are required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await post('onboarding', {
        employee_id: onboardForm.employee_id,
        employee_name: onboardForm.employee_name || undefined,
        start_date: onboardForm.start_date,
        target_completion_date: onboardForm.target_completion_date || undefined,
        assigned_buddy_name: onboardForm.assigned_buddy_name || undefined,
        template_id: onboardForm.template_id || undefined,
      });
      setShowCreate(false);
      setOnboardForm({ employee_id: '', employee_name: '', start_date: '', target_completion_date: '', assigned_buddy_name: '', template_id: '' });
      refetch();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to start onboarding');
    } finally {
      setCreating(false);
    }
  }

  const inputClass = 'w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground';

  if (loading) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-amber-500" /> Active Onboarding
        </h2>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
          <Plus className="w-4 h-4" /> Start Onboarding
        </button>
      </div>
      {(data?.onboarding ?? []).length === 0 ? (
        <EmptyState
          icon={<UserPlus className="w-12 h-12" />}
          title="No active onboarding"
          description="New hire onboarding processes will appear here."
          action={
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-foreground rounded-lg text-sm font-medium hover:bg-amber-600">
              <Plus className="w-4 h-4" /> Start Onboarding
            </button>
          }
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {(data?.onboarding ?? []).map(o => (
            <Card key={o.id}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{o.employee_name}</p>
                  <p className="text-xs text-muted-foreground">Started {o.start_date} &middot; Target {o.target_completion_date}</p>
                </div>
                <StatusBadge status={o.status} />
              </div>
              {/* Progress bar */}
              <div className="w-full bg-surface-3 rounded-full h-2 mb-2">
                <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${o.progress}%` }} />
              </div>
              <p className="text-xs text-muted-foreground">{o.progress}% complete &middot; {o.tasks.filter(t => t.completed).length}/{o.tasks.length} tasks</p>
              {o.assigned_buddy_name && <p className="text-xs text-muted-foreground mt-1">Buddy: {o.assigned_buddy_name}</p>}
              <button
                onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}
                className="mt-3 text-xs text-amber-500 hover:text-amber-400"
              >
                {expandedId === o.id ? 'Hide' : 'Show'} provisioning status
              </button>
              {expandedId === o.id && (
                <div className="mt-3">
                  <ProvisioningStatusPanel onboardingId={o.id} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create Onboarding Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Start Onboarding</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            {createError && <p className="text-red-400 text-sm">{createError}</p>}
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Employee ID *</label>
                <input value={onboardForm.employee_id} onChange={e => setOnboardForm(f => ({ ...f, employee_id: e.target.value }))} placeholder="Employee ID" className={inputClass} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Employee Name</label>
                <input value={onboardForm.employee_name} onChange={e => setOnboardForm(f => ({ ...f, employee_name: e.target.value }))} placeholder="John Smith" className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Start Date *</label>
                  <input type="date" value={onboardForm.start_date} onChange={e => setOnboardForm(f => ({ ...f, start_date: e.target.value }))} className={inputClass} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Target Completion</label>
                  <input type="date" value={onboardForm.target_completion_date} onChange={e => setOnboardForm(f => ({ ...f, target_completion_date: e.target.value }))} className={inputClass} />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Assigned Buddy</label>
                <input value={onboardForm.assigned_buddy_name} onChange={e => setOnboardForm(f => ({ ...f, assigned_buddy_name: e.target.value }))} placeholder="Buddy name" className={inputClass} />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
              <button onClick={() => void handleCreate()} disabled={creating} className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50">
                {creating ? 'Starting...' : 'Start Onboarding'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
